import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { UserName } from "@/components/badges/UserName";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { UserPlus, Briefcase, Building2, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SuggestedConnection {
  user_id: string;
  email: string;
  full_name: string;
  avatar_url: string | null;
  user_role: string;
  company: string;
  title: string;
  bio: string | null;
  is_admin_suggested: boolean;
  display_order: number;
}

interface SuggestedConnectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserId: string;
  currentUserRole: string;
}

export function SuggestedConnectionsModal({
  isOpen,
  onClose,
  currentUserId,
  currentUserRole,
}: SuggestedConnectionsModalProps) {
  const [suggestions, setSuggestions] = useState<SuggestedConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectingIds, setConnectingIds] = useState<Set<string>>(new Set());
  const [isCompleting, setIsCompleting] = useState(false);

  // Fetch suggestions when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSuggestions();
    }
  }, [isOpen, currentUserId]);

  const fetchSuggestions = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_suggested_connections_for_user", {
        p_user_id: currentUserId,
        p_random_count: 2,
      });

      if (error) throw error;
      setSuggestions(data || []);
    } catch (error) {
      console.error("Failed to fetch suggestions:", error);
      toast.error("Failed to load suggested connections");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (user: SuggestedConnection) => {
    setConnectingIds((prev) => new Set(prev).add(user.user_id));

    try {
      const message = `Hi ${user.full_name?.split(" ")[0] || "there"}, I'm new to EABConnect and would love to connect!`;

      const { data, error } = await supabase.rpc("create_connection_request", {
        p_recipient_id: user.user_id,
        p_message: message,
      });

      if (error) throw error;

      // The RPC returns { success: false, error } with HTTP 200 on failure, so
      // `error` alone is not enough to tell whether the request went through.
      const result = data as { success?: boolean; error?: string } | null;
      if (!result?.success) {
        throw new Error(result?.error || "Failed to send connection request");
      }

      toast.success(`Connection request sent to ${user.full_name}!`);

      // Remove user from list after successful connection
      setSuggestions((prev) => prev.filter((s) => s.user_id !== user.user_id));
    } catch (error: any) {
      console.error("Failed to connect:", error);
      toast.error(error.message || "Failed to send connection request");
    } finally {
      setConnectingIds((prev) => {
        const next = new Set(prev);
        next.delete(user.user_id);
        return next;
      });
    }
  };

  const handleComplete = useCallback(async () => {
    setIsCompleting(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ has_completed_suggested_connections: true })
        .eq("id", currentUserId);

      if (error) throw error;
    } catch (error) {
      console.error("Failed to update completion status:", error);
      // Don't block closing if update fails
    } finally {
      setIsCompleting(false);
      onClose();
    }
  }, [currentUserId, onClose]);

  const adminSuggested = suggestions.filter((s) => s.is_admin_suggested);
  const peerSuggested = suggestions.filter((s) => !s.is_admin_suggested);

  const peerLabel =
    currentUserRole === "mentor" ? "Mentors" : "Entrepreneurs";

  const renderUserCard = (user: SuggestedConnection) => {
    const isConnecting = connectingIds.has(user.user_id);

    return (
      <div
        key={user.user_id}
        className="flex flex-col sm:flex-row gap-4 p-4 border rounded-lg bg-card"
      >
        <UserAvatar
          avatarPath={user.avatar_url}
          fullName={user.full_name}
          className="h-14 w-14 shrink-0"
          thumbnailSize={64}
        />
        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-semibold text-foreground truncate">
            <UserName userId={user.user_id} name={user.full_name} />
          </p>
          {user.title && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Briefcase className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{user.title}</span>
            </p>
          )}
          {user.company && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Building2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{user.company}</span>
            </p>
          )}
          {user.bio && (
            <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
              {user.bio}
            </p>
          )}
        </div>
        <div className="shrink-0 self-start sm:self-center">
          <Button
            size="sm"
            onClick={() => handleConnect(user)}
            disabled={isConnecting}
            className="w-full sm:w-auto"
          >
            {isConnecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-1.5" />
                Connect
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleComplete()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl">
            Welcome to EABConnect! 🎉
          </DialogTitle>
          <DialogDescription>
            Start building your network by connecting with members of our
            community.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : suggestions.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No suggested connections available at this time.</p>
              <p className="text-sm mt-1">
                You can find more people to connect with in the Directory.
              </p>
            </div>
          ) : (
            <div className="space-y-6 py-2">
              {adminSuggested.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Connect with the EABConnect Team
                  </h3>
                  <div className="space-y-3">
                    {adminSuggested.map(renderUserCard)}
                  </div>
                </div>
              )}

              {peerSuggested.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Connect with Fellow {peerLabel}
                  </h3>
                  <div className="space-y-3">
                    {peerSuggested.map(renderUserCard)}
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleComplete}
            disabled={isCompleting}
            className="w-full sm:w-auto"
          >
            Skip for Now
          </Button>
          <Button
            onClick={handleComplete}
            disabled={isCompleting}
            className="w-full sm:w-auto"
          >
            {isCompleting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Continue to Dashboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
